import { Link, Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthProvider";
import { Alert, PageHeader, Tabs, buttonStyles } from "../../components/ui";
import { useBilling } from "../../lib/queries";

/* Route and key per tab; the words are in `ai.json` under `tabs`. */
const TABS = [
  { to: ".", key: "overview", end: true },
  { to: "models", key: "models" },
  { to: "activity", key: "activity" },
] as const;

/**
 * AI inference, as three destinations rather than one scroll.
 *
 * It was one page, and the router said why: "the catalogue is short and the usage
 * table is empty until somebody has made a call, so splitting them would mean two
 * screens that are each half a screen." That was true of a three-model catalogue
 * with no traffic. It stopped being true in both directions at once — the sync
 * discovers models an operator can list without a deploy, and a tenant running an
 * agent produces a usage view with its own filters, ranges and export.
 *
 * The split follows what somebody arrives wanting, which is never all three:
 *
 *   Overview   how do I call this, and is it working
 *   Models     what can I call, and what does it cost
 *   Activity   what has it cost me, and which of my keys spent it
 *
 * Routed rather than stateful, like the app and database detail pages, so each is
 * a real URL that survives a reload and can be linked in a support reply.
 *
 * The header action is "Create API key" on every tab, because that is the one
 * thing a customer cannot do from here and cannot proceed without: a browser
 * session token is not an inference credential, and the endpoint refuses it.
 */
export function AiLayout() {
  const { t } = useTranslation("ai");
  const { currentOrg } = useAuth();
  const billing = useBilling(currentOrg?.id);

  /*
   * On the layout rather than the Overview tab, because the wall is the same on
   * all three: the catalogue lists models that cannot be called yet, and the
   * activity table is empty for a reason that is not "you haven't tried".
   *
   * Shown only once the answer is known — `useBilling` is loading on first
   * paint, and a banner that appears a beat after the page has settled reads as
   * something having gone wrong. Withheld from an organization with nothing in
   * it, since there is nothing to explain to somebody who simply has not paid
   * yet: to them this is the ordinary empty balance the tabs already cover.
   *
   * Gated on having a balance rather than on having had the retired trial,
   * which is what it asked before — the same fault the billing page had. Nothing
   * writes `trial` entries any more, so an account funded by a promotion has
   * `trial.grantedMicros` of null and this banner never appeared for exactly the
   * customers whose calls were being refused.
   */
  const locked =
    billing.data && !billing.data.inferenceUnlocked && billing.data.balanceMicros > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        tone="ai"
        description={t("description")}
        action={
          <Link to="/settings/api-keys" className={buttonStyles("secondary")}>
            {t("createKey")}
          </Link>
        }
      />

      {locked && (
        <Alert tone="info">
          <span className="font-medium">{t("locked.lead")}</span>{" "}
          {billing.data!.promo.liveMicros > 0 ? t("locked.promo") : t("locked.granted")}{" "}
          {t("locked.unlock")}{" "}
          <Link to="/settings/billing" className="font-medium underline underline-offset-2">
            {t("locked.billing")}
          </Link>
        </Alert>
      )}

      <Tabs items={TABS.map((tab) => ({ ...tab, label: t(`tabs.${tab.key}`) }))} />

      <Outlet context={{ orgId: currentOrg?.id }} />
    </div>
  );
}
